"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import type { PromotionPopupConfig } from "@/lib/config/site-settings";

const STORAGE_KEY = "promotion-popup-dismissed";

export default function PromotionPopup({ config }: { config: PromotionPopupConfig }) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!config.enabled || !config.title.trim()) return;
    if (config.placement === "home" && pathname !== "/") return;

    const now = Date.now();
    const startsAt = config.startsAt ? Date.parse(config.startsAt) : null;
    const endsAt = config.endsAt ? Date.parse(config.endsAt) : null;
    if ((startsAt && now < startsAt) || (endsAt && now >= endsAt)) return;

    const storage = config.frequency === "browser" ? window.localStorage : window.sessionStorage;
    if (config.frequency !== "always" && storage.getItem(STORAGE_KEY) === "1") return;

    const timeout = window.setTimeout(() => {
      setOpen(true);
      if (config.id) {
        void fetch("/api/promotion/event", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ id: config.id, event: "impression" }),
          keepalive: true,
        });
      }
    }, config.delaySeconds * 1000);
    return () => window.clearTimeout(timeout);
  }, [config, pathname]);

  const dismiss = () => {
    if (config.frequency !== "always") {
      const storage = config.frequency === "browser" ? window.localStorage : window.sessionStorage;
      storage.setItem(STORAGE_KEY, "1");
    }
    setOpen(false);
  };

  if (!config.enabled || !config.title.trim() || (config.placement === "home" && pathname !== "/"))
    return null;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => (nextOpen ? setOpen(true) : dismiss())}>
      <DialogContent className="max-w-md p-8">
        {config.image && (
          <div className="relative -mx-8 -mt-8 mb-2 aspect-[16/7] overflow-hidden">
            <Image
              src={config.image}
              alt=""
              fill
              sizes="(max-width: 640px) 90vw, 448px"
              className="object-cover"
            />
          </div>
        )}
        <DialogTitle className="pr-8 text-2xl font-black tracking-tighter uppercase">
          {config.title}
        </DialogTitle>
        <DialogDescription className="mt-4 text-sm leading-6 whitespace-pre-line">
          {config.message}
        </DialogDescription>

        {config.code && (
          <div className="border-foreground/20 bg-muted mt-5 border border-dashed px-4 py-3 text-center font-mono text-sm font-bold tracking-[0.2em] uppercase">
            {config.code}
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          {config.ctaHref && config.ctaLabel && (
            <Link
              href={config.ctaHref}
              onClick={() => {
                if (config.id) {
                  void fetch("/api/promotion/event", {
                    method: "POST",
                    headers: { "content-type": "application/json" },
                    body: JSON.stringify({ id: config.id, event: "click" }),
                    keepalive: true,
                  });
                }
                dismiss();
              }}
              className="bg-foreground text-background hover:bg-foreground/85 focus-visible:ring-foreground inline-flex min-h-11 flex-1 items-center justify-center px-5 text-xs font-bold tracking-widest uppercase transition-colors focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {config.ctaLabel}
            </Link>
          )}
          <DialogClose
            onClick={dismiss}
            className="border-foreground hover:bg-muted focus-visible:ring-foreground inline-flex min-h-11 flex-1 items-center justify-center border px-5 text-xs font-bold tracking-widest uppercase transition-colors focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            Not now
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
